/* eslint-disable complexity */
/* eslint-disable no-restricted-imports */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import {
	useProductDetails,
	useProductDetailsAddWishList,
	useProductDetailsRemoveWishList,
} from '@/data/Content/ProductDetails';
import { ContentContext } from '@/data/context/content';
import { Button, ButtonBase, IconButton } from '@mui/material';
import React, { FC, Fragment, MouseEvent, useCallback, useContext, useMemo, useState } from 'react';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { Favorite, FavoriteBorder } from '@mui/icons-material';
import { useWishLists } from '@/data/Content/WishLists';
import { useInWishList } from '@/utils/useInWishList';
import { useNotifications } from '@/data/Content/Notifications';
import { AuthentificationFlow } from '@/components/blocks/AuthentificationFlow';
import { useUser } from '@/data/User';
import { isNotAuthorizedError } from '@/data/utils/isNotAuthorizedError';

type Props = {
	standalone?: boolean;
	isIconFavorite?: boolean;
	inThumbnail?: boolean;
	disabled?: boolean;
	isInVideo?: boolean;
};

export const ProductDetailsAddToWishList: FC<Props> = ({
	standalone = false,
	isIconFavorite = false,
	inThumbnail = false,
	disabled: _disabled,
	isInVideo = false,
}) => {
	const { selection, loginStatus, firstItemWithStock, sizeSelection } = useContext(
		ContentContext
	) as ReturnType<typeof useProductDetails>;

	const { showSnackbarMessage } = useNotifications();

	const dtId = useMemo(() => `wishlist-selection${standalone ? '-standalone' : ''}`, [standalone]);

	const currentSelection = useMemo(() => {
		if (sizeSelection.loaded && !sizeSelection.selected) {
			return firstItemWithStock;
		}

		return selection?.sku;
	}, [selection, sizeSelection, firstItemWithStock]);

	const [open, setOpen] = useState(false);

	const { addToWishList } = useProductDetailsAddWishList(currentSelection);
	const { removeToWishList } = useProductDetailsRemoveWishList();

	const { favoritesWishList, mutateWishLists, isLoading: loadingWishList } = useWishLists();
	const { mutateUser } = useUser();

	const inWishList = useInWishList(currentSelection?.partNumber, favoritesWishList);

	const onClickFavorites = useCallback(
		async (_event: MouseEvent<HTMLElement>) => {
			_event.stopPropagation();
			_event.preventDefault();

			if (!loginStatus) {
				setOpen(true);
				return;
			}

			if (!favoritesWishList || !currentSelection) {
				return;
			}

			try {
				if (!inWishList) {
					await addToWishList(currentSelection.partNumber, 1, favoritesWishList)();
					showSnackbarMessage('El artículo se ha añadido correctamente a la lista');
				} else {
					await removeToWishList(currentSelection.id, favoritesWishList)();
					showSnackbarMessage('El artículo se ha eliminado correctamente de la lista');
				}

				mutateWishLists();
			} catch (e) {
				if (isNotAuthorizedError(e)) {
					await mutateUser();
					setOpen(true);
					return;
				}
				throw e;
			}
		},

		[
			addToWishList,
			removeToWishList,
			showSnackbarMessage,
			mutateWishLists,
			mutateUser,
			inWishList,
			loginStatus,
			favoritesWishList,
			useInWishList,
			firstItemWithStock,
			currentSelection,
		]
	);

	return (
		<Fragment>
			{isIconFavorite ? (
				<ButtonBase
					onClick={onClickFavorites}
					id="pdp-favorite-button"
					aria-label="fingerprint"
					sx={{
						backgroundColor: '#F1F1F1',
						position: 'absolute',
						bottom: inThumbnail ? '115px' : isInVideo ? '120px' : '65px',
						left: inThumbnail ? undefined : '19px',
						right: inThumbnail ? '19px' : undefined,
						zIndex: 1001,
						width: '40px',
						height: '40px',
						borderRadius: '50%',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						'&:hover': {
							backgroundColor: '#e5e5e5',
						},
					}}
					disabled={_disabled || loadingWishList}
				>
					{inWishList ? (
						<Favorite sx={{ color: '#2686e2' }} />
					) : (
						<FavoriteBorder sx={{ color: '#505050', fontSize: '23px' }} />
					)}
				</ButtonBase>
			) : (
				<Button
					data-testid={dtId}
					id={dtId}
					onClick={onClickFavorites}
					disabled={_disabled || loadingWishList}
					sx={{
						background: '#F1F1F1',
						borderRadius: '22px',
						color: '#2C2C2C',
						fontSize: 'var(--font-size-body)',
						fontWeight: 'bold',
						height: '50px',
						minWidth: '153px',
					}}
					startIcon={<FavoriteBorderIcon />}
				>
					{'Favoritos'}
				</Button>
			)}
			<AuthentificationFlow
				open={open}
				onClose={() => setOpen(false)}
				type="wishlist"
				origin="saved_to_favorites"
			/>
		</Fragment>
	);
};
